import React from 'react';
import { v4 as UUID } from 'uuid';
import Button from '../Components/Buttons';
import EditableItemList from '../Components/EditableItemList';

function ArtifactParameters({
  parameters,
  setParameters,
  canEdit,
  isNew,
  artifactId,
  artifactName,
  artifactType,
  setLoading,
  showAlert,
  showConfirmation,
  updateArtifactById,
}) {
  const saveParametersToApi = async (updatedParams, successMessage) => {
    if (isNew) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', artifactName);
      formData.append('type', artifactType);
      formData.append(
        'parameters',
        JSON.stringify(
          updatedParams.map(({ key, name, description }) => ({ key, name, description })),
        ),
      );

      await updateArtifactById(artifactId, formData);
      showAlert('success', successMessage);
    } catch (error) {
      console.error('Error saving parameter:', error);
      showAlert('fail', 'Failed to save parameter');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = () => {
    const hasEmptyFields = parameters.some((p) => !p.name.trim() || !p.description.trim());

    if (hasEmptyFields) {
      showAlert('fail', 'Please ensure all parameters have both name and description filled.');
      return;
    }

    saveParametersToApi(parameters, 'Parameters saved successfully!');
  };

  const updateParameter = (itemId, field, value) => {
    setParameters((prev) =>
      prev.map((param) => {
        if (param.key === itemId) {
          return {
            ...param,
            name: field === 'keyword' ? value : param.name,
            description: field === 'definition' ? value : param.description,
          };
        }
        return param;
      }),
    );
  };

  const addParameter = () => {
    setParameters((prev) => [
      ...prev,
      {
        key: UUID(),
        name: '',
        description: '',
      },
    ]);
  };

  const removeParameter = (itemId) => {
    const paramToDelete = parameters.find((p) => p.key === itemId);
    if (!paramToDelete) return;

    if (!paramToDelete.name.trim() && !paramToDelete.description.trim()) {
      setParameters((prev) => prev.filter((p) => p.key !== itemId));
      return;
    }

    showConfirmation(`Are you sure you want to delete this parameter?`, async () => {
      const updatedParams = parameters.filter((param) => param.key !== itemId);
      setParameters(updatedParams);

      try {
        await saveParametersToApi(updatedParams, 'Parameter was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the parameter!');
      }
    });
  };

  const mappedItems = parameters.map((param) => ({
    id: param.key,
    keyword: param.name,
    definition: param.description,
  }));

  return (
    <div className="row mt-4">
      <div className="col-6 radius-yes p-2 bg-yes">
        <EditableItemList
          itemName="Parameter"
          items={mappedItems}
          totalItems={mappedItems.length}
          isDisabled={!canEdit}
          showActiveToggle={false}
          updateItem={updateParameter}
          addItem={addParameter}
          removeItem={removeParameter}
        />

        {canEdit && !isNew && (
          <div className="buttons-container d-flex justify-end mt-3 p-2">
            <Button variant="primary" size="medium" onClick={handleSaveAll}>
              Save All Parameters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ArtifactParameters;
